<template>
    <v-dialog :value="value" max-width="760px" persistent scrollable @input="$emit('input', $event)">
      <v-card>
  
        <!-- card header -->
        <v-card-title>
          <span class="headline">{{ editMode ? "تعديل نوع عقار" : "إضافة نوع عقار جديد" }}</span>
        </v-card-title>
  
        <!-- card body -->
        <v-card-text>
          <v-progress-linear v-if="fetching" indeterminate color="primary" class="mb-4" rounded />
  
          <!-- ref="form" mean that we can call form function using this.$refs.form. -->
           <!-- v-model="valid" means that the form is valid when all validation rules pass. -->
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="form.value"
                  label=" نوع العقار"
                  :rules="[rules.required]"
                  :error-messages="fieldErrors.value"
                  outlined
                  dense
                />
              </v-col>
     
            </v-row>
          </v-form>
        </v-card-text>
  
        <!-- card footer -->
        <v-card-actions>
          <!-- Pushes buttons to the right in the footer. -->
          <v-spacer />
          <v-btn text @click="close">إلغاء</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="fetching" @click="submit">
            {{ editMode ? "حفظ التعديلات" : "إضافة" }}
          </v-btn>
        </v-card-actions>
  
      </v-card>
    </v-dialog>
  </template>
  
  <script>
  import Axios from "axios";
  import { axiosInstance } from "@/axios/config";
  
  const emptyForm = () => ({
    value: "",
  });

  const emptyFieldErrors = () => ({
    value: [],
  });
  
  export default {
    name: "RealEstateTypesForm",
    // props are the properties that are passed to the component from the parent component.
    props: {
      value: { type: Boolean, default: false },
      editId: { type: [Number, String], default: null },
    },
    data() {
      return {
        valid: false,
        fetching: false,
        saving: false,
        form: emptyForm(),
        fieldErrors: emptyFieldErrors(),
        rules: {
          required: (v) => !!String(v || "").trim() || "الانواع مطلوب",
          requiredSelect: (v) =>
            (v !== null && v !== undefined && v !== "") || "هذا الحقل مطلوب",
        },
      };
    },
    computed: {
      editMode() {//true if editId is set, false otherwise
        return this.editId != null && this.editId !== "";
      },
      apiBase() {//the base URL for the API
        return `${this.$store.state.url}/employee/admin/real-estate-types`;
      },
    },
    //Runs code when something changes:
    watch: {
      value(open) {//runs when the dialog is opened or closed
        if (open) {
          this.resetForm();
          this.initForm();
        }
      },
    },
    methods: {
      async initForm() {
        await Promise.all([
          this.loadRealEstateType(),
        ]);
        if (this.editMode) {
          await this.loadRealEstateType();
        }
      },
      resetForm() {
        this.form = emptyForm();
        this.fieldErrors = emptyFieldErrors();
        this.$nextTick(() => {
          if (this.$refs.form) this.$refs.form.resetValidation();
        });
      },
      close() {
        this.$emit("input", false);
      },
      //=============================================================================
      //Loads the wsata product type options from the API
      async loadRealEstateType() {
        this.fetching = true;
        try {
          const res = await Axios.get(`${this.apiBase}/${this.editId}`, {
            headers: this.request_headers(),
          });
          this.form = res.data.payload || {};
        } catch (e) {
          this.showPopup("error", e.response?.data?.message || "تعذر تحميل البيانات");
        } finally {
          this.fetching = false;
        }
      },
      //=============================================================================
      buildPayload() {
        return {
          value: this.form.value.trim(),
        };
      },
      clearFieldErrors() {
        this.fieldErrors = emptyFieldErrors();
      },
      applyApiValidationErrors(errors = {}) {
        const next = emptyFieldErrors();
        Object.keys(next).forEach((field) => {
          if (errors[field]) {
            next[field] = Array.isArray(errors[field]) ? errors[field] : [errors[field]];
          }
        });
        this.fieldErrors = next;
      },
      firstValidationMessage(errors = {}) {
        const messages = Object.values(errors).flat().filter(Boolean);
        return messages[0] || null;
      },
      //=============================================================================
      async submit() {//submits the form data to the API
        if (!this.$refs.form.validate()) return;//if the form is not valid, return

        this.saving = true;
        this.clearFieldErrors();
        try {
          const payload = this.buildPayload();
          const headers = {
            ...this.request_headers(),
            "Content-Type": "application/json",
          };

          if (this.editMode) {//if we are editing, send a PUT request to the API
            await Axios.put(`${this.apiBase}/${this.editId}`, payload, { headers });
            this.showPopup("success", "تم تحديث الانواع");
          } else {//if we are adding, send a POST request to the API
            await Axios.post(this.apiBase, payload, { headers });
            this.showPopup("success", "تم إضافة الانواع");
          }
          /*
          Emit = “this child tells the parent something happened.”
  
          this.$emit('input', false);   // close dialog (works with v-model)
          this.$emit('saved');          // parent runs fetchItems()
          */
          this.$emit("saved");//emits the saved event to the parent component
          this.close();//closes the dialog
        } catch (e) {
          const apiErrors = e.response?.data?.errors;
          if (apiErrors) {
            this.applyApiValidationErrors(apiErrors);
            this.showPopup(
              "error",
              this.firstValidationMessage(apiErrors) || "يرجى تصحيح الحقول المطلوبة"
            );
          } else {
            this.showPopup("error", e.response?.data?.message || "تعذر الحفظ");
          }
        } finally {//finally block runs after the try or catch block, regardless of whether an error occurred
          this.saving = false;
        }
      },
      //=============================================================================
    },
  };
  </script>
  
  <!-- scoped means that the styles are only applied to this component -->
  <style scoped>
  .field-label {
    font-weight: 600;
    font-size: 0.9rem;
  }
      
  .theme--dark .field-label,
  .dark .field-label {
    color: rgba(255, 255, 255, 0.87);
  }
  </style>